import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Sparkles, Shield, Monitor, HeadphonesIcon, Wifi, Code, Wrench, Wand2, Crown } from 'lucide-react'
import SEO from '../components/SEO'

const Services = () => {
  const eliteServices = [
    {
      id: 'technology-concierge',
      title: 'Technology Concierge',
      subtitle: 'Your Personal Technology Advisor',
      description: 'A dedicated technology advisor available 24/7 to handle all your technology needs with the discretion and attention you deserve.',
      icon: Sparkles,
      path: '/services/technology-concierge',
      highlight: true
    },
    {
      id: 'white-glove-service',
      title: 'White Glove Service',
      subtitle: 'The Premium Praetorian Experience',
      description: 'Elite service standards with a dedicated team providing personalized, VIP treatment for every technology interaction.',
      icon: Crown,
      path: '/services/white-glove-service',
      highlight: true
    },
    {
      id: 'technology-magic',
      title: 'Technology Magic',
      subtitle: 'Making the Impossible, Possible',
      description: 'Breakthrough innovations and bespoke solutions that deliver experiences others say can\'t be done.',
      icon: Wand2,
      path: '/services/technology-magic',
      highlight: true
    },
    {
      id: 'managed-security',
      title: 'Managed Security',
      subtitle: 'Military-Grade Protection',
      description: 'Elite security measures protecting your digital assets across all properties, devices, and communications.',
      icon: Shield,
      path: '/services/managed-security',
      highlight: false
    },
    {
      id: 'managed-desktops',
      title: 'Managed Desktops',
      subtitle: 'Complete Device Management',
      description: 'Seamless device management across all your properties, ensuring your technology works perfectly wherever you are.',
      icon: Monitor,
      path: '/services/managed-desktops',
      highlight: false
    },
    {
      id: 'managed-help-desk',
      title: 'Managed Help Desk',
      subtitle: 'Your Personal Support Team',
      description: 'Dedicated support team available 24/7/365 who knows your preferences and delivers instant, confidential assistance.',
      icon: HeadphonesIcon,
      path: '/services/managed-help-desk',
      highlight: false
    },
    {
      id: 'network-operations',
      title: 'Network Operations',
      subtitle: 'Seamless Global Connectivity',
      description: 'Secure, high-performance networks across all your locations—homes, yachts, aircraft, and offices.',
      icon: Wifi,
      path: '/services/network-operations',
      highlight: false
    },
    {
      id: 'development-services',
      title: 'Development Services',
      subtitle: 'Bespoke Applications',
      description: 'Custom software solutions tailored to your specific needs, built with modern technologies and ongoing white glove support.',
      icon: Code,
      path: '/services/development-services',
      highlight: false
    },
    {
      id: 'project-recovery',
      title: 'Project Recovery',
      subtitle: 'Elite Project Rescue',
      description: 'Discreet, expert intervention to rescue troubled technology initiatives with strategic recovery and experienced leadership.',
      icon: Wrench,
      path: '/services/project-recovery',
      highlight: false
    }
  ]

  const highlightedServices = eliteServices.filter(s => s.highlight)
  const coreServices = eliteServices.filter(s => !s.highlight)

  return (
    <>
      <SEO 
        title="Elite Technology Services"
        description="Praetorian Works provides elite technology services for high net worth and ultra-high net worth individuals, including technology concierge, white glove service, managed security, and bespoke solutions."
        keywords="high net worth technology services, UHNW technology, technology concierge, white glove service, elite managed services, luxury technology support, private technology services, VIP tech support"
        canonical="/services"
      />
      
      <div className="pt-20">
        {/* Hero Section */}
        <section className="section-padding bg-black/30">
          <div className="container-max text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
            >
              Elite Technology <span className="text-primary-500">Services</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-neutral-300 max-w-4xl mx-auto leading-relaxed"
            >
              Technology services crafted exclusively for discerning individuals who demand 
              excellence, discretion, and VIP treatment in every interaction.
            </motion.p>
          </div>
        </section>

        {/* Signature Services */}
        <section className="section-padding bg-black/30">
          <div className="container-max">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center space-y-6 mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Signature <span className="text-primary-500">Services</span>
              </h2>
              <p className="text-lg text-neutral-300 max-w-3xl mx-auto">
                Our three flagship services define the Praetorian Works experience—personalized attention, 
                premium standards, and breakthrough innovation.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {highlightedServices.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link
                    to={service.path}
                    className="block h-full bg-secondary-800 rounded-2xl p-8 border border-primary-500/20 hover:border-primary-500/40 transition-all duration-300 group"
                  >
                    <div className="text-center space-y-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                        <service.icon className="w-8 h-8 text-secondary-900" />
                      </div>
                      
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-primary-400 transition-colors duration-300">
                          {service.title}
                        </h3>
                        <p className="text-primary-500 font-medium mb-4">
                          {service.subtitle}
                        </p>
                        <p className="text-neutral-300 leading-relaxed">
                          {service.description}
                        </p>
                      </div>

                      <div className="pt-4">
                        <span className="inline-flex items-center text-primary-500 group-hover:text-primary-400 font-medium transition-colors duration-300">
                          Learn More
                          <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Core Services */}
        <section className="section-padding bg-black/30">
          <div className="container-max">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center space-y-6 mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Comprehensive <span className="text-primary-500">Solutions</span>
              </h2>
              <p className="text-lg text-neutral-300 max-w-3xl mx-auto">
                Enterprise-grade managed services delivered with the personalized attention 
                and discretion you expect from Praetorian Works.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {coreServices.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link
                    to={service.path}
                    className="block h-full bg-secondary-900 rounded-xl p-6 border border-neutral-700 hover:border-primary-500/40 transition-all duration-300 group"
                  >
                    <div className="space-y-4">
                      <div className="w-12 h-12 bg-secondary-700 rounded-xl flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-primary-400 group-hover:to-primary-600 transition-all duration-300">
                        <service.icon className="w-6 h-6 text-primary-500 group-hover:text-secondary-900 transition-colors duration-300" />
                      </div>
                      
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-primary-400 transition-colors duration-300">
                          {service.title}
                        </h3>
                        <p className="text-primary-500 text-sm font-medium mb-3">
                          {service.subtitle}
                        </p>
                        <p className="text-neutral-400 text-sm leading-relaxed">
                          {service.description}
                        </p>
                      </div>

                      <div className="pt-2">
                        <span className="inline-flex items-center text-sm text-primary-500 group-hover:text-primary-400 font-medium transition-colors duration-300">
                          Explore Service
                          <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Value Proposition */}
        <section className="section-padding bg-black/30">
          <div className="container-max">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto text-center space-y-8"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                The Praetorian <span className="text-primary-500">Difference</span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                <div className="space-y-3">
                  <div className="w-12 h-12 bg-primary-500/10 rounded-xl flex items-center justify-center mx-auto">
                    <Shield className="w-6 h-6 text-primary-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Absolute Discretion</h3>
                  <p className="text-neutral-400">
                    Your privacy and security are paramount in everything we do
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="w-12 h-12 bg-primary-500/10 rounded-xl flex items-center justify-center mx-auto">
                    <Crown className="w-6 h-6 text-primary-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white">VIP Treatment</h3>
                  <p className="text-neutral-400">
                    Elite service standards with personalized attention to detail
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="w-12 h-12 bg-primary-500/10 rounded-xl flex items-center justify-center mx-auto">
                    <Sparkles className="w-6 h-6 text-primary-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Always Available</h3>
                  <p className="text-neutral-400">
                    24/7/365 support from your dedicated technology team
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding bg-gradient-to-br from-primary-400 via-primary-500 to-primary-600">
          <div className="container-max text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-secondary-900">
                Experience Elite Technology Services
              </h2>
              <p className="text-xl text-secondary-900/80 max-w-3xl mx-auto">
                Discover how Praetorian Works can elevate your technology experience 
                with personalized, discreet, and exceptional service.
              </p>
              <div className="pt-4">
                <Link 
                  to="/contact" 
                  className="btn-primary inline-flex items-center"
                >
                  Begin Your Journey
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default Services
